"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "apps/website/i18n/navigation";
import {
  SignupFlowSlug,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/constants";
import { useSignupFlowErrorStore, useSignupFlowStore } from "@./state";
import {
  buildRedirectUrl,
} from "apps/website/utils/abandoned-cart/buildRedirectUrl";

const INITIAL_SLUG = SignupFlowSlug.CAT_COUNT;

export function useSignupFlowReset(): void {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasHandledResetRef = useRef<string | null>(null);

  const {
    isStoreInitialised,
    versionMismatchDetected,
    getActiveCatIds,
  } = useSignupFlowStore((state) => ({
    isStoreInitialised: state.isStoreInitialised,
    versionMismatchDetected: state.versionMismatchDetected,
    getActiveCatIds: state.getActiveCatIds,
  }));

  useEffect(() => {
    if (!isStoreInitialised) {
      return;
    }

    // Reset the ref when pathname changes (new page navigation)
    if (hasHandledResetRef.current !== pathname) {
      hasHandledResetRef.current = null;
    }

    // Skip if we've already handled a reset for this pathname
    if (hasHandledResetRef.current === pathname) {
      return;
    }

    // Always reset store if version mismatch detected (even on initial slug)
    // This ensures old incompatible data is cleared immediately
    if (versionMismatchDetected) {
      useSignupFlowStore.getState().resetSignupFlow();
      useSignupFlowErrorStore.getState().clearAll();
      hasHandledResetRef.current = pathname;

      // Only redirect if not already on initial slug
      if (pathname !== INITIAL_SLUG) {
        router.replace(buildRedirectUrl(INITIAL_SLUG, searchParams));
      }
      return;
    }

    // If no cats and not on initial slug, reset and redirect to start.
    // Skip checkout — the store is intentionally cleared there before a hard navigation to the success page.
    if (getActiveCatIds().length === 0 && pathname !== INITIAL_SLUG && pathname !== SignupFlowSlug.CHECKOUT) {
      useSignupFlowStore.getState().resetSignupFlow();
      useSignupFlowErrorStore.getState().clearAll();
      hasHandledResetRef.current = pathname;

      router.replace(buildRedirectUrl(INITIAL_SLUG, searchParams));
    }
  }, [
    isStoreInitialised,
    versionMismatchDetected,
    pathname,
    router,
    searchParams,
    getActiveCatIds,
  ]);
}
